import { css } from '@emotion/react';
import { math } from 'polished';
import { Hover } from './mixins/_hover.mixin';
import { TableRowVariant } from './mixins/_table-row.mixin';
import { themeColorLevel } from './_functions';
import { breakpointInfix, breakpointNext, MediaBreakpointDown } from './mixins/_breakpoints.mixin';


export default ($) => {
  return css(Object.values(styles).map(style => style($)));
}


const styles = {}

//
// Basic Bootstrap table
//
styles.basic = ($) => css`
  .table {
    width: 100%;
    margin-bottom: ${$.spacer};
    color: ${$.tableColor};
    // Reset for nesting within parents with \`background-color\`.
    background-color: ${$.tableBg};

    th,
    td {
      padding: ${$.tableCellPadding};
      vertical-align: top;
      border-top: ${$.tableBorderWidth} solid ${$.tableBorderColor};
    }

    thead th {
      vertical-align: bottom;
      border-bottom: ${math(`2 * ${$.tableBorderWidth}`)} solid ${$.tableBorderColor};
    }

    tbody + tbody {
      border-top: ${math(`2 * ${$.tableBorderWidth}`)} solid ${$.tableBorderColor};
    }
  }
`;

//
// Condensed table w/ half padding
//
styles.half_padding = ($) => css`
  .table-sm {
    th,
    td {
      padding: ${$.tableCellPaddingSm};
    }
  }
`;

// Border versions
//
// Add or remove borders all around the table and between all the columns.
styles.borders = ($) => css`
  .table-bordered {
    border: ${$.tableBorderWidth} solid ${$.tableBorderColor};

    th,
    td {
      border: ${$.tableBorderWidth} solid ${$.tableBorderColor};
    }

    thead {
      th,
      td {
        border-bottom-width: ${math(`2 * ${$.tableBorderWidth}`)};
      }
    }
  }

  .table-borderless {
    th,
    td,
    thead th,
    tbody + tbody {
      border: 0;
    }
  }
`;

// Zebra-striping
//
// Default zebra-stripe styles (alternating gray and transparent backgrounds)
styles.zebraStriping = ($) => css`
  .table-striped {
    tbody tr:nth-of-type(${$.tableStripedOrder}) {
      background-color: ${$.tableAccentBg};
    }
  }
`;

// Hover effect
//
// Placed here since it has to come after the potential zebra striping
styles.hoverEffect = ($) => css`
  .table-hover {
    tbody tr {
      ${new Hover().include($, css`
        color: ${$.tableHoverColor};
        background-color: ${$.tableHoverBg};
      `)}
    }
  }
`;

// Table backgrounds
//
// Exact selectors below required to override `.table-striped` and prevent
// inheritance to nested tables.
styles.tableBackgrounds = ($) => {
  const rules = Object.keys($.themeColors).map((color) => {
    return new TableRowVariant(
      color,
      themeColorLevel($, color, $.tableBgLevel),
      themeColorLevel($, color, $.tableBorderLevel)
    ).include($);
  });

  rules.push(new TableRowVariant("active", $.tableActiveBg).include($))

  return css(rules);
}

// Dark styles
//
// Same table markup, but inverted color scheme: dark background and light text.

// stylelint-disable-next-line no-duplicate-selectors
styles.darkStyles = ($) => css`
  .table {
    .thead-dark {
      th {
        color: ${$.tableDarkColor};
        background-color: ${$.tableDarkBg};
        border-color: ${$.tableDarkBorderColor};
      }
    }

    .thead-light {
      th {
        color: ${$.tableHeadColor};
        background-color: ${$.tableHeadBg};
        border-color: ${$.tableBorderColor};
      }
    }
  }

  .table-dark {
    color: ${$.tableDarkColor};
    background-color: ${$.tableDarkBg};

    th,
    td,
    thead th {
      border-color: ${$.tableDarkBorderColor};
    }

    &.table-bordered {
      border: 0;
    }

    &.table-striped {
      tbody tr:nth-of-type(${$.tableStripedOrder}) {
        background-color: ${$.tableDarkAccentBg};
      }
    }

    &.table-hover {
      tbody tr {
        ${new Hover().include($, css`
          color: ${$.tableDarkHoverColor};
          background-color: ${$.tableDarkHoverBg};
        `)}
      }
    }
  }
`;


// Responsive tables
//
// Generate series of `.table-responsive-*` classes for configuring the screen
// size of where your table will overflow.
styles.responsiveTables = ($) => {
  const { gridBreakpoints } = $;
  const rules = Object.keys(gridBreakpoints).map(breakpoint => {
    const next = breakpointNext($, breakpoint, gridBreakpoints);
    const infix = breakpointInfix($, next, gridBreakpoints);

    return css `
      &${infix} {
        ${new MediaBreakpointDown(breakpoint).include($, css`
          display: block;
          width: 100%;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;

          // Prevent double border on horizontal scroll due to use of \`display: block;\`
          > .table-bordered {
            border: 0;
          }
        `)}
      }
    `;
  });

  return css`
    .table-responsive {
      ${css(rules)}
    }
  `;
};